import { LABEL_SOURCE_KEYS } from 'services/label-sources';
import { dynamicNamespace, equals, mergeFilters } from 'utils/dynamic';
import { apiRtk, DynamicService } from 'utils/service';
import { PatchPartial } from 'utils/types';
import { API_LABELS, ILabel, Label } from './models';

const REVALIDATE_KEY = 'Labels' as const;

const dynamic = dynamicNamespace<ILabel>();

export * from './models';

class Service extends DynamicService<Label> {
  async post(data: Label) {
    return super.post({ ...data, labelKey: data.labelKey.trim() });
  }
  async patch(data: Partial<Label>) {
    return super.patch({
      ...data,
      ...(data.labelKey
        ? {
            labelKey: data.labelKey.trim(),
          }
        : {}),
    });
  }
}

type ApiModel = Label;

export const ServiceLabels = new Service({
  getAll: API_LABELS.GET_ALL_DYNAMIC,
  post: API_LABELS.POST,
  patch: API_LABELS.PATCH,
  delete: API_LABELS.DELETE,
});

export const apiLabels = apiRtk.injectEndpoints({
  endpoints: (build) => ({
    getAppLabels: build.query<ApiModel[], string>({
      queryFn: async (languageID) => {
        try {
          const {
            data: { value },
          } = await ServiceLabels.getAllDynamic({
            select: dynamic.select('id', 'labelKey', 'title', 'labelSourceID'),
            filter: mergeFilters(
              dynamic.makeFilter('languageID', languageID, equals),
              dynamic.makeFilter('labelSource.key', LABEL_SOURCE_KEYS.CMS, equals),
              dynamic.makeFilter('isActive', true, equals),
            ).join('&&'),
          });
          return { data: value };
        } catch (e: any) {
          return { error: e };
        }
      },
      providesTags: (result, error, id) => [{ type: REVALIDATE_KEY, id }],
    }),
    getWebsiteLabels: build.query<ApiModel[], string>({
      queryFn: async (languageID) => {
        try {
          const {
            data: { value },
          } = await ServiceLabels.getAllDynamic({
            select: dynamic.select('id', 'labelKey', 'title', 'labelSourceID'),
            filter: mergeFilters(
              dynamic.makeFilter('languageID', languageID, equals),
              dynamic.makeFilter('labelSource.key', LABEL_SOURCE_KEYS.WEBSITE, equals),
              dynamic.makeFilter('isActive', true, equals),
            ).join('&&'),
          });
          return { data: value };
        } catch (e: any) {
          return { error: e };
        }
      },
      providesTags: (result, error, id) => [{ type: REVALIDATE_KEY, id }],
    }),
    getLabel: build.query<ApiModel, string>({
      queryFn: async (id) => {
        try {
          const { data } = await ServiceLabels.getDynamic(id);
          return { data };
        } catch (e: any) {
          return { error: e };
        }
      },
      providesTags: (result, error, id) => [{ type: REVALIDATE_KEY, id }],
    }),
    postLabel: build.mutation<void, ApiModel>({
      queryFn: async (data) => {
        try {
          await ServiceLabels.post(data);
          return { data: undefined };
        } catch (e: any) {
          return { error: e };
        }
      },
      invalidatesTags: [{ type: REVALIDATE_KEY }],
    }),
    patchLabel: build.mutation<void, PatchPartial<ApiModel, 'id'>>({
      queryFn: async (data) => {
        try {
          await ServiceLabels.patch(data);
          return { data: undefined };
        } catch (e: any) {
          return { error: e };
        }
      },
      invalidatesTags: (result, error, { id }) => [
        { type: REVALIDATE_KEY },
        { type: REVALIDATE_KEY, id },
      ],
    }),
    deleteLabel: build.mutation<void, PatchPartial<ApiModel, 'id'>>({
      queryFn: async (data) => {
        try {
          await ServiceLabels.delete(data);
          return { data: undefined };
        } catch (e: any) {
          return { error: e };
        }
      },
      invalidatesTags: (result, error, { id }) => [
        { type: REVALIDATE_KEY },
        { type: REVALIDATE_KEY, id },
      ],
    }),
  }),
});
